import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layers, ArrowRight, AlertTriangle, GitBranch, ListChecks } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useTenant } from "@/context/TenantContext";
import { Card } from "@/components/ui/card";
import { PageHero } from "@/components/PageHero";
import { Badge } from "@/components/ui/badge";

function scoreColor(p) {
  if (p >= 60) return "bg-red-100 text-red-700 border-red-200";
  if (p >= 40) return "bg-amber-100 text-amber-800 border-amber-200";
  return "bg-slate-100 text-slate-600 border-slate-200";
}

export default function Redundancies() {
  const { tenantId, currentOrg } = useTenant();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tenantId) { setData(null); return; }
    setLoading(true);
    api.get(`/redundancies?tenant_id=${tenantId}`).then((r) => setData(r.data))
      .catch(() => toast.error("Errore nell'analisi delle ridondanze"))
      .finally(() => setLoading(false));
  }, [tenantId]);

  return (
    <div className="space-y-6">
      <PageHero label="Analisi contenuti" title="Rilevazione ridondanze"
        subtitle={<>Coppie di documenti con contenuti sovrapposti in {tenantId ? <strong>{currentOrg?.nome}</strong> : "un cliente"}, ordinate per grado di similarità.</>} />

      {!tenantId ? (
        <Card className="p-12 text-center border-dashed">
          <Layers className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Seleziona un cliente dal menu in alto per analizzare le ridondanze.</p>
        </Card>
      ) : loading ? (
        <p className="text-sm text-slate-500">Analisi dei contenuti in corso...</p>
      ) : !data || data.pairs.length === 0 ? (
        <Card className="p-12 text-center border-dashed" data-testid="no-redundancies">
          <Layers className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Nessuna sovrapposizione significativa tra i {data?.documents_analyzed ?? 0} documenti analizzati.</p>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="redundancy-list">
          <p className="text-sm text-slate-500">{data.pairs.length} coppie segnalate su {data.documents_analyzed} documenti.</p>
          {data.pairs.map((p, i) => (
            <Card key={i} data-testid={`redundancy-pair-${i}`} className="p-5 border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-amber-600">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="text-sm font-medium">Possibile sovrapposizione</span>
                </div>
                <Badge className={scoreColor(p.percent)}>{p.percent}% simile</Badge>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-center">
                <Link to={`/documenti/${p.doc_a.id}`} className="p-3 rounded-md border border-slate-200 hover:border-blue-400 hover:bg-blue-50 transition-colors">
                  <div className="flex items-center gap-2"><Badge variant="outline" className="font-mono">{p.doc_a.codice}</Badge><span className="text-xs text-slate-500">{p.doc_a.tipo}</span></div>
                  <div className="text-sm font-medium mt-1">{p.doc_a.titolo}</div>
                </Link>
                <ArrowRight className="h-5 w-5 text-slate-300 mx-auto hidden md:block" />
                <Link to={`/documenti/${p.doc_b.id}`} className="p-3 rounded-md border border-slate-200 hover:border-blue-400 hover:bg-blue-50 transition-colors">
                  <div className="flex items-center gap-2"><Badge variant="outline" className="font-mono">{p.doc_b.codice}</Badge><span className="text-xs text-slate-500">{p.doc_b.tipo}</span></div>
                  <div className="text-sm font-medium mt-1">{p.doc_b.titolo}</div>
                </Link>
              </div>
              {(p.shared_terms.length > 0 || p.shared_requirements.length > 0 || p.shared_processes.length > 0) && (
                <div className="mt-4 pt-4 border-t border-slate-100 space-y-2">
                  {p.shared_terms.length > 0 && (
                    <div className="flex items-start gap-2 flex-wrap">
                      <span className="label-caps mt-1">Termini comuni</span>
                      {p.shared_terms.map((t) => <Badge key={t} variant="secondary" className="font-normal">{t}</Badge>)}
                    </div>
                  )}
                  {p.shared_requirements.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap text-sm text-slate-600">
                      <ListChecks className="h-3.5 w-3.5 text-blue-700" /> Requisiti in comune:
                      {p.shared_requirements.map((r) => <Badge key={r} variant="outline" className="font-mono text-[11px]">{r}</Badge>)}
                    </div>
                  )}
                  {p.shared_processes.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap text-sm text-slate-600">
                      <GitBranch className="h-3.5 w-3.5 text-blue-700" /> Processi in comune:
                      {p.shared_processes.map((r) => <Badge key={r} variant="outline" className="text-[11px]">{r}</Badge>)}
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
